import { forwardRef, type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type Props = {
  children: ReactNode;
  className?: string;
};

// Horizontal scroll container for the pipeline columns. On Windows (the
// primary audience) the styled native scrollbar from `.kanban-scroll-rail`
// is always visible and is the main "there is more sideways" cue. The
// edge-fade overlays add a second cue that points to the hidden side and
// also covers touch/macOS, where native scrollbars auto-hide.
export const KanbanScrollRail = forwardRef<HTMLDivElement, Props>(
  ({ children, className }, ref) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [overflow, setOverflow] = useState({ left: false, right: false });

    const setRefs = (el: HTMLDivElement | null) => {
      innerRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    };

    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const update = () => {
        const left = el.scrollLeft > 4;
        const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
        setOverflow((prev) =>
          prev.left === left && prev.right === right ? prev : { left, right }
        );
      };
      update();
      el.addEventListener("scroll", update, { passive: true });
      const ro = new ResizeObserver(update);
      ro.observe(el);
      const mo = new MutationObserver(update);
      mo.observe(el, { childList: true, subtree: true });
      return () => {
        el.removeEventListener("scroll", update);
        ro.disconnect();
        mo.disconnect();
      };
    }, []);

    return (
      <div className="relative min-h-0 flex-1">
        <div
          ref={setRefs}
          className={cn(
            "kanban-scroll-rail flex h-full gap-3 overflow-x-auto p-3 [scroll-padding-left:0.75rem]",
            className
          )}
        >
          {children}
        </div>
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute bottom-3 left-0 top-0 w-6 bg-gradient-to-r from-white to-transparent transition-opacity duration-200",
            overflow.left ? "opacity-100" : "opacity-0"
          )}
        />
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute bottom-3 right-0 top-0 w-6 bg-gradient-to-l from-white to-transparent transition-opacity duration-200",
            overflow.right ? "opacity-100" : "opacity-0"
          )}
        />
      </div>
    );
  }
);
KanbanScrollRail.displayName = "KanbanScrollRail";
